import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ApiService } from "./api.service";
import {
  CreateEndconsumerModel,
  LoginEndconsumerResponseData,
  LoginRequestData,
  LogoutRequestData,
  ResetPassRequestData,
  ConfirmResetPassRequestData, CreateTeamModel, LoginTeamResponseData,
} from "../models/user.model";
import { BehaviorSubject, Observable, tap, catchError, throwError } from "rxjs";
import { isPlatformBrowser } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _apiService: ApiService = inject(ApiService);
  private _platformId = inject(PLATFORM_ID);
  private _userSubject: BehaviorSubject<LoginEndconsumerResponseData | ''> = new BehaviorSubject<LoginEndconsumerResponseData | ''>(
    this.get()
  );
  user$: Observable<LoginEndconsumerResponseData | ''> = this._userSubject.asObservable();
  private _currentUserType!: 'team' | 'end-consumer' | null;

  constructor() {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  get(): any { // LoginEndconsumerResponseData | LoginTeamResponseData
    if (this.isBrowser()) {
      const userData: string | null = localStorage.getItem('user-' + this._currentUserType );
      return userData ? JSON.parse(userData) : '';
    }
    return '';
  }

  set(userData: any): void {
    if (this.isBrowser() && this._currentUserType) {
      localStorage.setItem('user-' + this._currentUserType, JSON.stringify(userData));
      this._userSubject.next(userData);
    }
  }

  delete(): void {
    if (this.isBrowser()) {
      if (this._currentUserType) {
        localStorage.removeItem('user-' + this._currentUserType);
      } else {
        localStorage.removeItem('user-team');
        localStorage.removeItem('user-end-consumer');
      }
      this._userSubject.next('');
    }
  }

  async changeCurrentUser(userType: 'team' | 'end-consumer'): Promise<void> {
    this._currentUserType = userType;

    if (this.isBrowser()) {
      const userDataStr = localStorage.getItem('user-' + userType);
      const userData = userDataStr ? JSON.parse(userDataStr) : '';
      this._userSubject.next(userData);
    }
  }

  createEndconsumer(userData: CreateEndconsumerModel): Observable<any> {
    return this._apiService.post('/end-consumers/register/', userData, true);
  }

  updateEndconsumer(userData: any): Observable<any> {
    return this._apiService.patch('/end-consumers/me/', userData);
  }

  getTeam(): Observable<any> {
    return this._apiService.get('/teams/me/', 'team').pipe(
      catchError((error: HttpErrorResponse) => {
        const errorDetail = error?.error?.detail;
        const errorCode = error?.error?.code;

        if (
          errorDetail === 'User not found' ||
          errorCode === 'user_not_found'
        ) {
          // Automatically logout when user is not found
          // This will clear localStorage and trigger AuthService to clear access token via user$ subscription
          this.delete();
        }

        return throwError(() => error);
      })
    );
  }

  getTeamSubscription(): Observable<any> {
    return this._apiService.get('/subscriptions/', 'team');
  }

  createTeam(userData: CreateTeamModel, code?: string): Observable<any> {
    const url: string = code ? `/teams/register/?code=${code}` : '/teams/register/';
    return this._apiService.post(url, userData, true);
  }

  updateTeam(userData: any): Observable<any> {
    return this._apiService.patch('/teams/me/', userData);
  }

  login(loginData: LoginRequestData): Observable<any> {
    return this._apiService.post('/auth/login/', loginData, true).pipe(
      tap((response: any) => {
        const userType = response?.user?.user_type;
        if (userType === 'team' || userType === 'end_consumer') {
          this._currentUserType = userType.replace('_', '-');
        } else {
          this._currentUserType = null;
        }
      })
    );
  }

  logout(logoutData: LogoutRequestData): Observable<any> {
    return this._apiService.post('/auth/logout/', logoutData);
  }

  resetPass(resetPassReqData: ResetPassRequestData): Observable<any> {
    return this._apiService.post('/auth/reset-password/?platform=browser', resetPassReqData);
  }

  confirmResetPass(confirmResetPassReqData: ConfirmResetPassRequestData): Observable<any> {
    return this._apiService.post('/auth/confirm-reset-password/', confirmResetPassReqData);
  }

  otpRequest(): Observable<any> {
    return this._apiService.post('/auth/request-otp/', {});
  }

  otpVerify(data: { otp_code: string }): Observable<any> {
    return this._apiService.post('/auth/verify-otp/', data);
  }

  getAreaAvailability(): Observable<any> {
    return this._apiService.get('/end-consumers/me/area-availability/');
  }
}
